<template>
  <div class="session-card" :class="[typeClass, { 'has-report': hasReport, 'is-match': isMatch }]" @click="$emit('openSession', session)">
    <div class="session-type-bar"></div>
    <div class="session-content">
      <div class="session-header">
        <span class="session-type-label">{{ typeLabel }}</span>
        <span v-if="hasReport" class="status-icon done"><i class="bi bi-check-circle-fill"></i></span>
        <span v-else-if="isPast" class="status-icon overdue"><i class="bi bi-exclamation-circle-fill"></i></span>
        <span v-else class="status-icon upcoming"><i class="bi bi-clock"></i></span>
      </div>
      <div class="session-time">
        <i class="bi bi-clock"></i>
        {{ formatTime(session.kickOffTime) }}
        <span v-if="session.endTime"> - {{ formatTime(session.endTime) }}</span>
      </div>
      <div v-if="isMatch && session.opposition" class="session-opposition">
        v {{ session.opposition }}
      </div>
      <div v-if="session.drill_count > 0" class="session-drills-badge">
        <i class="bi bi-clipboard-check"></i> {{ session.drill_count }} drills
      </div>
      <div v-if="hasReport" class="session-rpe">
        RPE: <strong>{{ session.effort }}</strong>/10
      </div>
      <div v-if="attendanceLabel" class="session-attendance" :class="attendanceClass">
        {{ attendanceLabel }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "SessionCard",
  props: {
    session: { type: Object, required: true },
  },
  computed: {
    isMatch() {
      return this.session.gameType !== "Training";
    },
    sessionType() {
      if (this.isMatch) return "match";
      return this.session.training_session_type || "Team Training";
    },
    typeClass() {
      const map = {
        "Team Training": "type-team",
        "Own Training": "type-own",
        "S&C": "type-sc",
        "match": "type-match",
      };
      return map[this.sessionType] || "type-team";
    },
    typeLabel() {
      if (this.isMatch) return this.session.gameType || "Match";
      return this.sessionType;
    },
    hasReport() {
      return !!this.session.report_id;
    },
    isPast() {
      return moment(this.session.matchDate).isBefore(moment(), "day");
    },
    attendanceLabel() {
      if (parseInt(this.session.going) === 1) return "Going";
      if (parseInt(this.session.notGoing) === 1) return "Not going";
      if (parseInt(this.session.injured) === 1) return "Injured";
      return null;
    },
    attendanceClass() {
      if (parseInt(this.session.going) === 1) return "att-going";
      if (parseInt(this.session.notGoing) === 1) return "att-not-going";
      if (parseInt(this.session.injured) === 1) return "att-injured";
      return "";
    },
  },
  methods: {
    formatTime(dateStr) {
      if (!dateStr) return "";
      // Session times are stored as wall-clock-UTC strings (…THH:MM:00.000Z);
      // they MUST be read with .utc() or they shift by the viewer's TZ offset
      // (e.g. +1h in BST). See the time-storage contract in views/booking.
      return moment(dateStr).utc().format("HH:mm");
    },
  },
};
</script>

<style scoped lang="scss">
.session-card {
  display: flex;
  gap: 0;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.session-type-bar {
  width: 4px;
  flex-shrink: 0;
}

.type-team .session-type-bar { background: #3b82f6; }
.type-own .session-type-bar { background: #22c55e; }
.type-sc .session-type-bar { background: #f59e0b; }
.type-match .session-type-bar { background: #ef4444; }

.session-content {
  flex: 1;
  padding: 8px 10px;
  min-width: 0;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.session-type-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.type-team .session-type-label { color: #3b82f6; }
.type-own .session-type-label { color: #22c55e; }
.type-sc .session-type-label { color: #f59e0b; }
.type-match .session-type-label { color: #ef4444; }

.status-icon {
  font-size: 0.85rem;
  &.done { color: #22c55e; }
  &.overdue { color: #f59e0b; }
  &.upcoming { color: #9ca3af; }
}

.session-time {
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
  i { font-size: 0.7rem; }
}

.session-opposition {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
  margin-top: 2px;
}

.session-drills-badge {
  font-size: 0.68rem;
  color: #6b7280;
  margin-top: 3px;
  i { font-size: 0.65rem; }
}

.session-rpe {
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 2px;
}

.session-attendance {
  font-size: 0.62rem;
  font-weight: 700;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.att-going { color: #16a34a; }
  &.att-not-going { color: #dc2626; }
  &.att-injured { color: #d97706; }
}

.is-match {
  border-color: #fecaca;
  background: #fef2f2;
}
</style>
